import { useEffect, useState } from "react";
import { api, errMsg } from "@/lib/api";
import { useApp } from "@/context/AppContext";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { DemoBadge, EmptyState } from "@/components/shared";
import { AreaChart, Area, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip as RTooltip, ResponsiveContainer } from "recharts";
import { BarChart3 } from "lucide-react";
import { toast } from "sonner";

export default function Analytics() {
  const { activeWebsite } = useApp();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      if (!activeWebsite) { setLoading(false); return; }
      setLoading(true);
      try {
        const { data: d } = await api.get(`/websites/${activeWebsite.id}/analytics`);
        setData(d);
      } catch (e) { toast.error(errMsg(e)); }
      setLoading(false);
    })();
  }, [activeWebsite]);

  if (loading) return <Skeleton className="h-64 w-full" />;
  const hasData = (data?.daily || []).length > 0 || (data?.ga_daily || []).length > 0;
  if (!hasData) {
    return <EmptyState icon={BarChart3} title="No analytics data yet" message="Connect Search Console and GA4 (or load labelled demo data) to populate analytics." testId="analytics-empty" />;
  }
  const isDemo = data.data_source === "demo";

  return (
    <div className="space-y-4">
      <div>
        <h1 className="text-xl font-bold tracking-tight">Analytics {isDemo && <DemoBadge />}</h1>
        <p className="text-xs text-muted-foreground">Search performance (Search Console) and organic behaviour (GA4), last 28 days.</p>
      </div>

      <div className="grid gap-4 lg:grid-cols-2">
        <Card className="shadow-none">
          <CardHeader className="pb-2"><CardTitle className="text-sm font-semibold">Search: clicks & impressions</CardTitle></CardHeader>
          <CardContent>
            <div className="h-52">
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={data.daily}>
                  <CartesianGrid stroke="hsl(214 18% 88%)" strokeOpacity={0.6} vertical={false} />
                  <XAxis dataKey="date" tick={{ fontSize: 10 }} tickLine={false} axisLine={false} minTickGap={30} />
                  <YAxis tick={{ fontSize: 10 }} tickLine={false} axisLine={false} width={44} />
                  <RTooltip contentStyle={{ fontSize: 12, borderRadius: 8 }} />
                  <Area type="monotone" dataKey="impressions" stroke="hsl(215 14% 65%)" fill="hsl(215 14% 65% / 0.15)" strokeWidth={1.5} />
                  <Area type="monotone" dataKey="clicks" stroke="hsl(218 66% 36%)" fill="hsl(218 66% 36% / 0.12)" strokeWidth={2} />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>

        <Card className="shadow-none">
          <CardHeader className="pb-2"><CardTitle className="text-sm font-semibold">GA4: organic sessions & conversions</CardTitle></CardHeader>
          <CardContent>
            {(data.ga_daily || []).length ? (
              <div className="h-52">
                <ResponsiveContainer width="100%" height="100%">
                  <AreaChart data={data.ga_daily}>
                    <CartesianGrid stroke="hsl(214 18% 88%)" strokeOpacity={0.6} vertical={false} />
                    <XAxis dataKey="date" tick={{ fontSize: 10 }} tickLine={false} axisLine={false} minTickGap={30} />
                    <YAxis tick={{ fontSize: 10 }} tickLine={false} axisLine={false} width={44} />
                    <RTooltip contentStyle={{ fontSize: 12, borderRadius: 8 }} />
                    <Area type="monotone" dataKey="sessions" stroke="hsl(218 66% 36%)" fill="hsl(218 66% 36% / 0.12)" strokeWidth={2} />
                    <Area type="monotone" dataKey="conversions" stroke="hsl(142 52% 36%)" fill="hsl(142 52% 36% / 0.12)" strokeWidth={2} />
                  </AreaChart>
                </ResponsiveContainer>
              </div>
            ) : (
              <EmptyState title="No GA4 data" message="Connect Google Analytics 4 to see organic sessions and conversions." />
            )}
          </CardContent>
        </Card>
      </div>

      <div className="grid gap-4 lg:grid-cols-2">
        <Card className="shadow-none">
          <CardHeader className="pb-2"><CardTitle className="text-sm font-semibold">Top landing pages (search)</CardTitle></CardHeader>
          <CardContent className="scc-table-wrap p-0">
            <Table>
              <TableHeader><TableRow><TableHead>Page</TableHead><TableHead className="text-right">Clicks</TableHead><TableHead className="text-right">Impr.</TableHead><TableHead className="text-right">Pos.</TableHead></TableRow></TableHeader>
              <TableBody>
                {data.top_pages.map((p) => (
                  <TableRow key={p.page}>
                    <TableCell className="max-w-[240px] truncate font-mono text-[11px]">{p.page}</TableCell>
                    <TableCell className="text-right text-xs tabular-nums">{p.clicks.toLocaleString()}</TableCell>
                    <TableCell className="text-right text-xs tabular-nums">{p.impressions.toLocaleString()}</TableCell>
                    <TableCell className="text-right text-xs tabular-nums">{p.position}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>

        <Card className="shadow-none">
          <CardHeader className="pb-2"><CardTitle className="text-sm font-semibold">Top converting pages (GA4)</CardTitle></CardHeader>
          <CardContent className="scc-table-wrap p-0">
            {(data.top_converting || []).length ? (
              <Table>
                <TableHeader><TableRow><TableHead>Page</TableHead><TableHead className="text-right">Sessions</TableHead><TableHead className="text-right">Conv.</TableHead><TableHead className="text-right">Rate</TableHead></TableRow></TableHeader>
                <TableBody>
                  {data.top_converting.map((p) => (
                    <TableRow key={p.page}>
                      <TableCell className="max-w-[240px] truncate font-mono text-[11px]">{p.page}</TableCell>
                      <TableCell className="text-right text-xs tabular-nums">{p.sessions.toLocaleString()}</TableCell>
                      <TableCell className="text-right text-xs tabular-nums">{p.conversions}</TableCell>
                      <TableCell className="text-right text-xs tabular-nums">{(p.rate * 100).toFixed(1)}%</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            ) : <div className="p-4 text-xs text-muted-foreground">No conversion data yet.</div>}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
